import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import {
  User,
  Subscription,
  Section,
  Template,
  GenericSection,
  DocumentTemplate,
  ApplicantData,
  LocalFile,
  CloudFile,
  CloudConnections,
  Notification,
  SessionMetadata,
  BrandingSettings,
  ChatSettings,
  NuanceSettings,
  ChainConfig,
  ChainProgressEvent,
  StyleOptions,
  ImageProvider,
  StyleReference,
  GeneratedImage,
  CoverDesign,
  ImageBlock,
  DocumentConfig,
  VisualBlock,
  ParsedDataset,
  StoryOutline,
  StoryBible,
} from '../types';
import { sessionsApi } from '../services/api';

// Default values
const defaultApplicantData: ApplicantData = {
  beneficiaryName: '',
  beneficiaryNationality: '',
  currentLocation: '',
  petitionerName: '',
  petitionerType: 'Corporation',
  petitionerState: '',
  petitionerAddress: '',
  visaType: 'general',
  industry: 'Technology',
  complexity: 'Standard',
  priorityDate: '',
  filingDate: '',
  caseNumber: '',
  attorneyName: '',
  additionalInfo: '',
  llmProvider: 'openai',
  llmModel: 'gpt-4',
  customFields: [],
  genericFields: {},
};

const defaultGenericSections: GenericSection[] = [
  {
    id: 'primary',
    title: 'Primary Party',
    fields: [
      { id: 'primary_name', label: 'Full Name', value: '', type: 'text' },
      { id: 'primary_role', label: 'Title/Role', value: '', type: 'text' },
      { id: 'primary_location', label: 'Address/Location', value: '', type: 'text' },
    ],
    isExpanded: true,
    order: 1,
  },
  {
    id: 'secondary',
    title: 'Secondary Party',
    fields: [
      { id: 'secondary_name', label: 'Organization/Name', value: '', type: 'text' },
      { id: 'secondary_address', label: 'Address', value: '', type: 'longText' },
    ],
    isExpanded: true,
    order: 2,
  },
  {
    id: 'document',
    title: 'Document Details',
    fields: [
      { id: 'reference_number', label: 'Reference Number', value: '', type: 'text' },
      { id: 'prepared_by', label: 'Prepared By', value: '', type: 'text' },
    ],
    isExpanded: true,
    order: 3,
  },
];

interface AppState {
  // User state
  user: User | null;
  subscription: Subscription | null;
  isAuthenticated: boolean;

  // Application data
  applicantData: ApplicantData;
  sections: Section[];
  genericSections: GenericSection[];
  systemPrompt: string;
  documentType: string;
  currentLetterTemplateId: string;

  // Files
  localFiles: (LocalFile | CloudFile)[];
  selectedDocuments: (LocalFile | CloudFile)[];
  cloudConnections: CloudConnections;

  // Templates
  allTemplates: Template[];
  documentTemplates: DocumentTemplate[];

  // UI state
  isLoading: boolean;
  notifications: Notification[];
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;

  // Session management
  currentSessionId: string;
  currentSessionName: string;
  sessions: SessionMetadata[];
  isSessionSidebarOpen: boolean;
  hasUnsavedChanges: boolean;

  // Settings
  chatSettings: ChatSettings;
  brandingSettings: BrandingSettings;

  // Chain generation & writing style
  nuanceSettings: NuanceSettings;
  chainConfig: ChainConfig;
  chainProgress: ChainProgressEvent | null;
  isChainGenerating: boolean;
  referenceText: string;
  styleOptions: StyleOptions;
  referenceFileName: string;

  // Story mode state
  storyOutline: StoryOutline | null;
  storyBible: StoryBible | null;

  // Image generation & publishing
  imageProvider: ImageProvider;
  styleReferences: StyleReference[];
  selectedStyleRefs: string[];
  selectedCharacterRefs: string[];
  generatedImages: GeneratedImage[];
  coverDesign: CoverDesign | null;
  imageBlocks: Record<number, ImageBlock[]>; // keyed by sectionId

  // Data visualizations
  visualBlocks: Record<number, VisualBlock[]>; // keyed by sectionId
  parsedDatasets: ParsedDataset[];

  // Document configuration (Project Wizard)
  documentConfig: DocumentConfig | null;
  showProjectWizard: boolean;
  pageViewZoom: number;
  pageViewMode: 'single' | 'twoPage';

  // Subscription modal
  showSubscriptionModal: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setSubscription: (subscription: Subscription | null) => void;
  setApplicantData: (data: Partial<ApplicantData>) => void;
  setSections: (sections: Section[]) => void;
  updateSection: (id: number, updates: Partial<Section>) => void;
  addSection: (afterId?: number) => void;
  removeSection: (id: number) => void;
  setGenericSections: (sections: GenericSection[]) => void;
  updateGenericField: (sectionId: string, fieldId: string, value: string) => void;
  setDocumentType: (type: string) => void;
  setCurrentLetterTemplate: (templateId: string) => void;
  setSystemPrompt: (prompt: string) => void;
  setLocalFiles: (files: (LocalFile | CloudFile)[]) => void;
  setSelectedDocuments: (docs: (LocalFile | CloudFile)[]) => void;
  addDocumentToSelection: (doc: LocalFile | CloudFile) => void;
  removeDocumentFromSelection: (id: string) => void;
  setCloudConnections: (connections: Partial<CloudConnections>) => void;
  setAllTemplates: (templates: Template[]) => void;
  setDocumentTemplates: (templates: DocumentTemplate[]) => void;
  setLoading: (loading: boolean) => void;
  addNotification: (message: string, type: 'success' | 'error' | 'info') => void;
  removeNotification: (id: number) => void;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  setSessions: (sessions: SessionMetadata[]) => void;
  toggleSessionSidebar: () => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  saveCurrentSession: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<void>;
  createNewSession: () => void;
  updateSessionName: (name: string) => void;
  updateChatSettings: (settings: Partial<ChatSettings>) => void;
  updateBrandingSettings: (settings: Partial<BrandingSettings>) => void;
  updateNuanceSettings: (settings: Partial<NuanceSettings>) => void;
  updateChainConfig: (config: Partial<ChainConfig>) => void;
  setChainProgress: (progress: ChainProgressEvent | null) => void;
  setChainGenerating: (generating: boolean) => void;
  setReferenceStyle: (text: string, fileName: string) => void;
  updateStyleOptions: (options: Partial<StyleOptions>) => void;
  clearReferenceStyle: () => void;
  // Story mode actions
  setStoryOutline: (outline: StoryOutline | null) => void;
  setStoryBible: (bible: StoryBible | null) => void;
  clearStoryState: () => void;
  logout: () => void;

  // Image actions
  setImageProvider: (provider: ImageProvider) => void;
  setStyleReferences: (refs: StyleReference[]) => void;
  addStyleReference: (ref: StyleReference) => void;
  removeStyleReference: (id: string) => void;
  toggleStyleRefSelection: (id: string) => void;
  toggleCharacterRefSelection: (id: string) => void;
  clearRefSelections: () => void;
  setGeneratedImages: (images: GeneratedImage[]) => void;
  addGeneratedImage: (image: GeneratedImage) => void;
  removeGeneratedImage: (id: string) => void;
  setCoverDesign: (cover: CoverDesign | null) => void;
  setImageBlocks: (blocks: Record<number, ImageBlock[]>) => void;
  addImageBlock: (sectionId: number, block: ImageBlock) => void;
  updateImageBlock: (sectionId: number, blockId: string, updates: Partial<ImageBlock>) => void;
  removeImageBlock: (sectionId: number, blockId: string) => void;

  // Document configuration actions
  setDocumentConfig: (config: DocumentConfig | null) => void;
  setShowProjectWizard: (show: boolean) => void;
  clearDocumentConfig: () => void;
  setPageViewZoom: (zoom: number) => void;
  setPageViewMode: (mode: 'single' | 'twoPage') => void;

  // Visual block actions
  setVisualBlocks: (blocks: Record<number, VisualBlock[]>) => void;
  addVisualBlock: (sectionId: number, block: VisualBlock) => void;
  updateVisualBlock: (sectionId: number, blockId: string, updates: Partial<VisualBlock>) => void;
  removeVisualBlock: (sectionId: number, blockId: string) => void;

  // Parsed dataset actions
  addParsedDataset: (dataset: ParsedDataset) => void;
  removeParsedDataset: (datasetId: string) => void;
  clearParsedDatasets: () => void;

  // Subscription modal actions
  setShowSubscriptionModal: (show: boolean) => void;
}

export const useStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        subscription: null,
        isAuthenticated: false,
        applicantData: defaultApplicantData,
        sections: [],
        genericSections: defaultGenericSections,
        systemPrompt: '',
        documentType: 'general',
        currentLetterTemplateId: '',
        localFiles: [],
        selectedDocuments: [],
        cloudConnections: { googleDrive: false, dropbox: false, oneDrive: false },
        allTemplates: [],
        documentTemplates: [],
        isLoading: false,
        notifications: [],
        isSidebarOpen: true,
        isSidebarCollapsed: false,
        currentSessionId: uuidv4(),
        currentSessionName: 'Untitled Document',
        sessions: [],
        isSessionSidebarOpen: false,
        hasUnsavedChanges: false,
        chatSettings: {
          enabled: true,
          position: 'bottom-right',
          saveHistory: true,
        },
        brandingSettings: {
          customLogoEnabled: false,
          accentColor: '#3b4e6d',
          accentTextColor: '#ffffff',
        },
        nuanceSettings: {
          formality: 3,
          detailLevel: 3,
          perspective: 'neutral',
          confidence: 3,
          paragraphStyle: 'medium',
          transitions: 'moderate',
          repetitionHandling: 'reduce',
        },
        chainConfig: {
          enabled: false,
          contextDepth: 10,  // Higher default for better story continuity
          contextFormat: 'full',
          continueFlow: true,
          // Story mode defaults
          storyMode: false,
          generateOutline: true,
          trackCharacters: true,
          creativityLevel: 'medium',
        },
        chainProgress: null,
        isChainGenerating: false,
        referenceText: '',
        styleOptions: {
          copyTone: true,
          copyFormatting: true,
          copySentenceStructure: false,
          copyParagraphLength: false,
          copyVocabulary: false,
        },
        referenceFileName: '',

        // Story mode state
        storyOutline: null,
        storyBible: null,

        // Image generation & publishing
        imageProvider: 'openai' as ImageProvider,
        styleReferences: [],
        selectedStyleRefs: [],
        selectedCharacterRefs: [],
        generatedImages: [],
        coverDesign: null,
        imageBlocks: {},

        // Data visualizations
        visualBlocks: {},
        parsedDatasets: [],

        // Document configuration (Project Wizard)
        documentConfig: null,
        showProjectWizard: false,
        pageViewZoom: 1,
        pageViewMode: 'single',

        // Subscription modal
        showSubscriptionModal: false,

        // Actions
        setUser: (user) => set({ user, isAuthenticated: !!user }),
        setSubscription: (subscription) => set({ subscription }),

        setApplicantData: (data) =>
          set((state) => ({
            applicantData: { ...state.applicantData, ...data },
            hasUnsavedChanges: true,
          })),

        setSections: (sections) => set({ sections, hasUnsavedChanges: true }),

        updateSection: (id, updates) =>
          set((state) => ({
            sections: state.sections.map((s) =>
              s.id === id ? { ...s, ...updates } : s
            ),
            hasUnsavedChanges: true,
          })),

        addSection: (afterId) =>
          set((state) => {
            const newSection: Section = {
              id: Date.now(),
              title: 'Custom Section',
              prompt: 'Enter your custom prompt...',
              content: '',
              isEditing: false,
              isGenerating: false,
              documents: [],
              imageConfig: {
                enabled: false,
                provider: state.imageProvider || 'openai',
                size: 'square',
              },
            };

            if (afterId) {
              const index = state.sections.findIndex((s) => s.id === afterId);
              const newSections = [...state.sections];
              newSections.splice(index + 1, 0, newSection);
              return { sections: newSections, hasUnsavedChanges: true };
            }

            return { sections: [...state.sections, newSection], hasUnsavedChanges: true };
          }),

        removeSection: (id) =>
          set((state) => ({
            sections: state.sections.filter((s) => s.id !== id),
            hasUnsavedChanges: true,
          })),

        setGenericSections: (genericSections) => set({ genericSections }),

        updateGenericField: (sectionId, fieldId, value) =>
          set((state) => {
            const genericSections = state.genericSections.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  fields: section.fields.map((field) =>
                    field.id === fieldId ? { ...field, value } : field
                  ),
                };
              }
              return section;
            });

            // Also update genericFields in applicantData
            const genericFields = { ...state.applicantData.genericFields };
            if (!genericFields[sectionId]) {
              genericFields[sectionId] = {};
            }
            const section = genericSections.find((s) => s.id === sectionId);
            const field = section?.fields.find((f) => f.id === fieldId);
            if (field) {
              genericFields[sectionId][field.label] = value;
            }

            return {
              genericSections,
              applicantData: { ...state.applicantData, genericFields },
              hasUnsavedChanges: true,
            };
          }),

        setDocumentType: (documentType) => set({ documentType }),

        setCurrentLetterTemplate: (templateId) =>
          set((state) => {
            const template = state.allTemplates.find((t) => t.id === templateId);
            if (template) {
              const newSections: Section[] = template.sections.map((section, index) => ({
                id: Date.now() + index,
                title: section.title,
                prompt: section.prompt,
                content: '',
                isEditing: false,
                isGenerating: false,
                documents: [],
                imageConfig: {
                  enabled: false,
                  provider: state.imageProvider || 'openai',
                  size: 'square',
                },
              }));
              return {
                currentLetterTemplateId: templateId,
                sections: newSections,
              };
            }
            return { currentLetterTemplateId: templateId };
          }),

        setSystemPrompt: (systemPrompt) => set({ systemPrompt }),

        setLocalFiles: (localFiles) => set({ localFiles }),

        setSelectedDocuments: (selectedDocuments) => set({ selectedDocuments }),

        addDocumentToSelection: (doc) =>
          set((state) => {
            if (!state.selectedDocuments.some((d) => d.id === doc.id)) {
              return { selectedDocuments: [...state.selectedDocuments, doc] };
            }
            return {};
          }),

        removeDocumentFromSelection: (id) =>
          set((state) => ({
            selectedDocuments: state.selectedDocuments.filter((d) => d.id !== id),
          })),

        setCloudConnections: (connections) =>
          set((state) => ({
            cloudConnections: { ...state.cloudConnections, ...connections },
          })),

        setAllTemplates: (allTemplates) => set({ allTemplates }),
        setDocumentTemplates: (documentTemplates) => set({ documentTemplates }),
        setLoading: (isLoading) => set({ isLoading }),

        addNotification: (message, type) =>
          set((state) => ({
            notifications: [
              { id: Date.now(), message, type },
              ...state.notifications,
            ],
          })),

        removeNotification: (id) =>
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          })),

        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
        toggleSidebarCollapse: () =>
          set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),

        setSessions: (sessions) => set({ sessions }),
        toggleSessionSidebar: () =>
          set((state) => ({ isSessionSidebarOpen: !state.isSessionSidebarOpen })),
        setHasUnsavedChanges: (hasUnsavedChanges) => set({ hasUnsavedChanges }),

        saveCurrentSession: async () => {
          const state = get();
          const sessionState = {
            applicantData: state.applicantData,
            sections: state.sections,
            genericSections: state.genericSections,
            systemPrompt: state.systemPrompt,
            documentType: state.documentType,
            currentLetterTemplateId: state.currentLetterTemplateId,
            selectedDocuments: state.selectedDocuments,
            localFiles: state.localFiles,
            allTemplates: state.allTemplates,
            documentTemplates: state.documentTemplates,
            imageBlocks: state.imageBlocks,
            coverDesign: state.coverDesign,
            documentConfig: state.documentConfig,
            visualBlocks: state.visualBlocks,
            parsedDatasets: state.parsedDatasets,
          };

          try {
            await sessionsApi.update(state.currentSessionId, {
              name: state.currentSessionName,
              documentType: state.documentType,
              state: sessionState,
            });
            set({ hasUnsavedChanges: false });
            get().addNotification('Session saved', 'success');
          } catch {
            // If update fails (session doesn't exist), create new
            const newSession = await sessionsApi.create({
              name: state.currentSessionName,
              documentType: state.documentType,
              state: sessionState,
            });
            set({
              currentSessionId: newSession.id,
              hasUnsavedChanges: false,
            });
            get().addNotification('Session saved', 'success');
          }
        },

        loadSession: async (sessionId) => {
          try {
            const session = await sessionsApi.get(sessionId);
            set({
              currentSessionId: session.id,
              currentSessionName: session.name,
              applicantData: session.state.applicantData || defaultApplicantData,
              sections: session.state.sections || [],
              genericSections: session.state.genericSections || defaultGenericSections,
              systemPrompt: session.state.systemPrompt || '',
              documentType: session.state.documentType || 'general',
              currentLetterTemplateId: session.state.currentLetterTemplateId || '',
              selectedDocuments: session.state.selectedDocuments || [],
              localFiles: session.state.localFiles || [],
              allTemplates: session.state.allTemplates || get().allTemplates,
              documentTemplates: session.state.documentTemplates || get().documentTemplates,
              imageBlocks: session.state.imageBlocks || {},
              coverDesign: session.state.coverDesign || null,
              documentConfig: session.state.documentConfig || null,
              visualBlocks: session.state.visualBlocks || {},
              parsedDatasets: session.state.parsedDatasets || [],
              hasUnsavedChanges: false,
            });
            get().addNotification(`Loaded: ${session.name}`, 'success');
          } catch {
            get().addNotification('Failed to load session', 'error');
          }
        },

        createNewSession: () => {
          set({
            currentSessionId: uuidv4(),
            currentSessionName: 'Untitled Document',
            applicantData: defaultApplicantData,
            sections: [],
            genericSections: defaultGenericSections,
            selectedDocuments: [],
            localFiles: [],
            imageBlocks: {},
            coverDesign: null,
            documentConfig: null,
            visualBlocks: {},
            parsedDatasets: [],
            showProjectWizard: true,
            hasUnsavedChanges: false,
          });
        },

        updateSessionName: (name) =>
          set({
            currentSessionName: name,
            hasUnsavedChanges: true,
          }),

        updateChatSettings: (settings) =>
          set((state) => ({
            chatSettings: { ...state.chatSettings, ...settings },
          })),

        updateBrandingSettings: (settings) =>
          set((state) => ({
            brandingSettings: { ...state.brandingSettings, ...settings },
          })),

        updateNuanceSettings: (settings) =>
          set((state) => ({
            nuanceSettings: { ...state.nuanceSettings, ...settings },
          })),

        updateChainConfig: (config) =>
          set((state) => ({
            chainConfig: { ...state.chainConfig, ...config },
          })),

        setChainProgress: (chainProgress) => set({ chainProgress }),

        setChainGenerating: (isChainGenerating) => set({ isChainGenerating }),

        setReferenceStyle: (referenceText, referenceFileName) => set({ referenceText, referenceFileName }),

        updateStyleOptions: (options) =>
          set((state) => ({
            styleOptions: { ...state.styleOptions, ...options },
          })),

        clearReferenceStyle: () => set({ referenceText: '', referenceFileName: '' }),

        // Story mode actions
        setStoryOutline: (storyOutline) => set({ storyOutline }),
        setStoryBible: (storyBible) => set({ storyBible }),
        clearStoryState: () => set({ storyOutline: null, storyBible: null }),

        // Image actions
        setImageProvider: (imageProvider) => set({ imageProvider }),

        setStyleReferences: (styleReferences) => set({ styleReferences }),

        addStyleReference: (ref) =>
          set((state) => ({
            styleReferences: [...state.styleReferences, ref],
          })),

        removeStyleReference: (id) =>
          set((state) => ({
            styleReferences: state.styleReferences.filter((r) => r.id !== id),
            selectedStyleRefs: state.selectedStyleRefs.filter((rid) => rid !== id),
            selectedCharacterRefs: state.selectedCharacterRefs.filter((rid) => rid !== id),
          })),

        toggleStyleRefSelection: (id) =>
          set((state) => ({
            selectedStyleRefs: state.selectedStyleRefs.includes(id)
              ? state.selectedStyleRefs.filter((rid) => rid !== id)
              : [...state.selectedStyleRefs, id],
          })),

        toggleCharacterRefSelection: (id) =>
          set((state) => ({
            selectedCharacterRefs: state.selectedCharacterRefs.includes(id)
              ? state.selectedCharacterRefs.filter((rid) => rid !== id)
              : [...state.selectedCharacterRefs, id],
          })),

        clearRefSelections: () =>
          set({ selectedStyleRefs: [], selectedCharacterRefs: [] }),

        setGeneratedImages: (generatedImages) => set({ generatedImages }),

        addGeneratedImage: (image) =>
          set((state) => ({
            generatedImages: [image, ...state.generatedImages],
          })),

        removeGeneratedImage: (id) =>
          set((state) => ({
            generatedImages: state.generatedImages.filter((i) => i.id !== id),
          })),

        setCoverDesign: (coverDesign) => set({ coverDesign, hasUnsavedChanges: true }),

        setImageBlocks: (imageBlocks) => set({ imageBlocks }),

        addImageBlock: (sectionId, block) =>
          set((state) => ({
            imageBlocks: {
              ...state.imageBlocks,
              [sectionId]: [...(state.imageBlocks[sectionId] || []), block],
            },
            hasUnsavedChanges: true,
          })),

        updateImageBlock: (sectionId, blockId, updates) =>
          set((state) => ({
            imageBlocks: {
              ...state.imageBlocks,
              [sectionId]: (state.imageBlocks[sectionId] || []).map((b) =>
                b.id === blockId ? { ...b, ...updates } : b
              ),
            },
            hasUnsavedChanges: true,
          })),

        removeImageBlock: (sectionId, blockId) =>
          set((state) => ({
            imageBlocks: {
              ...state.imageBlocks,
              [sectionId]: (state.imageBlocks[sectionId] || []).filter(
                (b) => b.id !== blockId
              ),
            },
            hasUnsavedChanges: true,
          })),

        // Document configuration actions
        setDocumentConfig: (documentConfig) => set({ documentConfig, hasUnsavedChanges: true }),

        setShowProjectWizard: (showProjectWizard) => set({ showProjectWizard }),

        clearDocumentConfig: () => set({ documentConfig: null, hasUnsavedChanges: true }),

        setPageViewZoom: (pageViewZoom) => set({ pageViewZoom }),

        setPageViewMode: (pageViewMode) => set({ pageViewMode }),

        // Visual block actions
        setVisualBlocks: (visualBlocks) => set({ visualBlocks }),

        addVisualBlock: (sectionId, block) =>
          set((state) => ({
            visualBlocks: {
              ...state.visualBlocks,
              [sectionId]: [...(state.visualBlocks[sectionId] || []), block],
            },
            hasUnsavedChanges: true,
          })),

        updateVisualBlock: (sectionId, blockId, updates) =>
          set((state) => ({
            visualBlocks: {
              ...state.visualBlocks,
              [sectionId]: (state.visualBlocks[sectionId] || []).map((b) =>
                b.id === blockId ? ({ ...b, ...updates } as VisualBlock) : b
              ),
            },
            hasUnsavedChanges: true,
          })),

        removeVisualBlock: (sectionId, blockId) =>
          set((state) => ({
            visualBlocks: {
              ...state.visualBlocks,
              [sectionId]: (state.visualBlocks[sectionId] || []).filter(
                (b) => b.id !== blockId
              ),
            },
            hasUnsavedChanges: true,
          })),

        // Parsed dataset actions
        addParsedDataset: (dataset) =>
          set((state) => ({
            parsedDatasets: [...state.parsedDatasets, dataset],
          })),

        removeParsedDataset: (datasetId) =>
          set((state) => ({
            parsedDatasets: state.parsedDatasets.filter((d) => d.id !== datasetId),
          })),

        clearParsedDatasets: () => set({ parsedDatasets: [] }),

        // Subscription modal actions
        setShowSubscriptionModal: (showSubscriptionModal) => set({ showSubscriptionModal }),

        logout: () =>
          set({
            user: null,
            subscription: null,
            isAuthenticated: false,
          }),
      }),
      {
        name: 'docwriter-store',
        partialize: (state) => ({
          brandingSettings: state.brandingSettings,
          chatSettings: state.chatSettings,
          nuanceSettings: state.nuanceSettings,
          chainConfig: state.chainConfig,
          imageProvider: state.imageProvider,
          pageViewMode: state.pageViewMode,
        }),
      }
    ),
    { name: 'DocWriter' }
  )
);
